import { alternarFavorito, moverFavorito } from '@construtora/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  chavesMenuFavoritos,
  useGravarMenuFavoritos,
  useMenuFavoritos,
} from '@/shared/api/menu-favoritos.api';
import type { SecaoDoMenu, Submenu } from '@/shared/lib/cotas';
import { chaveDoSubmenu, lerSubmenusGravados, submenuAbertoPorPadrao } from './submenus';

const CHAVE_SUBMENUS = 'construtora.menu.submenus';
const CHAVE_FAVORITOS_FECHADO = 'construtora.menu.favoritos-fechado';

/**
 * O menu de Favoritos nasce **aberto**: é o atalho que o usuário montou, e
 * fechado por padrão ele custaria o clique que a estrela existe para poupar.
 * Por isso o que se grava é o "fechado", e a ausência da chave é aberto.
 */
function favoritosFechadosNoNavegador(): boolean {
  try {
    return localStorage.getItem(CHAVE_FAVORITOS_FECHADO) === 'true';
  } catch {
    return false;
  }
}

function lerDoNavegador(): Record<string, boolean> {
  try {
    return lerSubmenusGravados(localStorage.getItem(CHAVE_SUBMENUS));
  } catch {
    // Aba anônima que proíbe o acesso: sem preferência, vale o padrão.
    return {};
  }
}

function gravarNoNavegador(valor: Record<string, boolean>): void {
  try {
    localStorage.setItem(CHAVE_SUBMENUS, JSON.stringify(valor));
  } catch {
    // Preferência de menu não vale um erro em voz alta.
  }
}

export interface MenuPersonalizado {
  /** As cotas favoritas, na ordem gravada. Vazio enquanto carrega. */
  readonly favoritos: readonly string[];
  readonly alternarFavorito: (cota: string) => void;
  readonly mover: (cota: string, direcao: 'subir' | 'descer') => void;
  /** A última gravação falhou — o menu voltou ao que está no servidor. */
  readonly falhouAoGravar: boolean;

  /** Modo em que as estrelas e as setas aparecem. */
  readonly organizando: boolean;
  readonly alternarOrganizacao: () => void;

  /** O menu de Favoritos está aberto. Estado de janela, como as seções. */
  readonly favoritosAbertos: boolean;
  readonly alternarFavoritos: () => void;

  readonly submenuAberto: (secao: SecaoDoMenu, submenu: Submenu) => boolean;
  readonly alternarSubmenu: (secao: SecaoDoMenu, submenu: Submenu) => void;
}

/**
 * O estado do menu que é do usuário: favoritos, o modo de organizar e os
 * submenus abertos (`.spec/015`, decisão 75).
 *
 * Fora do `AppShell` pelo limite de complexidade e porque é um assunto só: o
 * shell decide **onde** o menu fica; isto decide **o que** está aberto nele.
 *
 * Os dois estados moram em lugares diferentes, de propósito. Favorito vai para
 * o servidor — é a lista que a pessoa montou, e ela vale em qualquer máquina.
 * Submenu aberto fica no navegador, como as seções: é estado de janela.
 */
export function useMenuPersonalizado(cotaAberta: string | null): MenuPersonalizado {
  const cliente = useQueryClient();
  const consulta = useMenuFavoritos();
  // `mutate` é estável entre renderizações; o objeto da mutação, não.
  const { mutate: gravar, isError: falhouAoGravar } = useGravarMenuFavoritos();
  const [organizando, setOrganizando] = useState(false);
  const [submenus, setSubmenus] = useState<Record<string, boolean>>(lerDoNavegador);
  const [favoritosFechados, setFavoritosFechados] = useState(favoritosFechadosNoNavegador);

  /*
    Lê a lista do cache na hora do clique, e não da renderização: dois cliques
    seguidos antes de a tela redesenhar partiriam os dois da mesma lista, e o
    segundo desfaria o primeiro. A gravação otimista escreve no cache já no
    `mutate()`, então o segundo clique encontra o primeiro lá.
  */
  const atuais = useCallback(
    (): readonly string[] =>
      cliente.getQueryData<{ cotas: string[] }>(chavesMenuFavoritos.meus)?.cotas ?? [],
    [cliente],
  );

  const alternar = useCallback(
    (cota: string): void => {
      gravar({ cotas: alternarFavorito(atuais(), cota) });
    },
    [atuais, gravar],
  );

  /*
    O grupo é a lista inteira: desde a decisão 105 o menu de Favoritos mostra
    o vetor gravado como ele é, e o vizinho na tela é o vizinho no vetor. Até
    ali a troca pulava as cotas de outro módulo, que ficavam em outra seção.
  */
  const mover = useCallback(
    (cota: string, direcao: 'subir' | 'descer'): void => {
      gravar({ cotas: moverFavorito(atuais(), cota, direcao, () => true) });
    },
    [atuais, gravar],
  );

  const submenuAberto = useCallback(
    (secao: SecaoDoMenu, submenu: Submenu): boolean => {
      // Organizando, tudo aberto: a estrela da tela que está num submenu
      // fechado não se alcança, e o modo existe justamente para marcá-la.
      if (organizando) return true;

      const gravado = submenus[chaveDoSubmenu(secao.titulo, submenu.titulo)];
      return gravado ?? submenuAbertoPorPadrao(submenu, cotaAberta);
    },
    [organizando, submenus, cotaAberta],
  );

  const alternarSubmenu = useCallback(
    (secao: SecaoDoMenu, submenu: Submenu): void => {
      const chave = chaveDoSubmenu(secao.titulo, submenu.titulo);
      const aberto = submenuAberto(secao, submenu);

      setSubmenus((atual) => {
        const proximo = { ...atual, [chave]: !aberto };
        gravarNoNavegador(proximo);
        return proximo;
      });
    },
    [submenuAberto],
  );

  return {
    favoritos: consulta.data?.cotas ?? [],
    alternarFavorito: alternar,
    mover,
    falhouAoGravar,
    organizando,
    alternarOrganizacao: () => {
      setOrganizando((atual) => !atual);
    },
    // Organizando, aberto: é nele que as setas de ordem aparecem.
    favoritosAbertos: organizando || !favoritosFechados,
    alternarFavoritos: () => {
      setFavoritosFechados((fechado) => {
        const proximo = !fechado;
        try {
          localStorage.setItem(CHAVE_FAVORITOS_FECHADO, String(proximo));
        } catch {
          // Preferência de menu não vale um erro em voz alta.
        }
        return proximo;
      });
    },
    submenuAberto,
    alternarSubmenu,
  };
}
